import Swiper from 'swiper'
import { Navigation, Keyboard, Thumbs } from 'swiper/modules'

// Album tabs, client-side paging and the lightbox for the /gallery page.
// Every photo is in the DOM already (loading="lazy" keeps the bytes off the
// wire until a thumbnail is actually shown), so paging is just toggling
// `hidden` on chunks of the grid.

const DEFAULT_PAGE_SIZE = 24

const setTabState = (tab, active) => {
  const [label, underline] = tab.children
  tab.setAttribute('aria-selected', active)
  label.classList.toggle('font-bold', active)
  label.classList.toggle('text-[#203e23]', active)
  label.classList.toggle('font-medium', !active)
  label.classList.toggle('text-[rgba(32,62,35,0.61)]', !active)
  underline.classList.toggle('bg-[#006139]', active)
}

const initTabs = (root) => {
  const tabs = root.querySelectorAll('[data-gallery-tab]')
  const panels = root.querySelectorAll('[data-gallery-panel]')

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      tabs.forEach((other) => setTabState(other, other === tab))
      panels.forEach((panel) => {
        const active = panel.dataset.galleryPanel === tab.dataset.galleryTab
        panel.classList.toggle('hidden', !active)
        panel.classList.toggle('flex', active)
      })
    })
  })
}

// The skeleton is added here rather than server-rendered, so a bundle that never
// runs still leaves plain visible thumbnails instead of a grid of empty boxes.
const initImageLoading = (root) => {
  root.querySelectorAll('[data-gallery-open] img').forEach((img) => {
    // `complete` alone lies for deferred lazy images; naturalWidth confirms bytes.
    if (img.complete && img.naturalWidth) return

    const button = img.parentElement
    img.classList.add('opacity-0')
    button.classList.add('animate-pulse', 'bg-[#e5e7eb]')

    // `error` too, so a broken image stops pulsing instead of hanging on it.
    const settled = () => {
      img.classList.remove('opacity-0')
      button.classList.remove('animate-pulse', 'bg-[#e5e7eb]')
    }
    img.addEventListener('load', settled, { once: true })
    img.addEventListener('error', settled, { once: true })
  })
}

// The tab strip already scrolls on touch; give a mouse the same drag.
const initDragScroll = (root) => {
  const strip = root.querySelector('[data-gallery-tabs]')
  if (!strip) return

  let startX = 0
  let startLeft = 0
  let suppressClick = false

  const onUp = () => {
    strip.classList.remove('cursor-grabbing')
    window.removeEventListener('pointermove', onMove)
    window.removeEventListener('pointerup', onUp)
    window.removeEventListener('pointercancel', onUp)
  }

  const onMove = (event) => {
    // A button released outside the window fires no pointerup; this ends the drag.
    if (event.buttons === 0) return onUp()
    const delta = event.clientX - startX
    if (Math.abs(delta) > 5) suppressClick = true
    strip.scrollLeft = startLeft - delta
  }

  strip.addEventListener('pointerdown', (event) => {
    // Cleared ahead of the guards so a touch tap also starts from a clean slate.
    suppressClick = false
    if (event.pointerType === 'touch' || event.button !== 0) return
    startX = event.clientX
    startLeft = strip.scrollLeft
    strip.classList.add('cursor-grabbing')
    // On window rather than setPointerCapture: capture retargets the following
    // click to the strip, which would stop tab buttons ever being selected.
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
  })

  // One-shot, so it only eats the click that ends a drag — keyboard and touch
  // activation of a tab later on must still go through.
  strip.addEventListener(
    'click',
    (event) => {
      if (!suppressClick) return
      suppressClick = false
      event.preventDefault()
      event.stopPropagation()
    },
    true
  )
}

const initPager = (panel) => {
  const grid = panel.querySelector('[data-gallery-grid]')
  const pager = panel.querySelector('[data-gallery-pager]')
  if (!grid || !pager) return

  const pageSize = Number(grid.dataset.galleryPageSize) || DEFAULT_PAGE_SIZE
  const photos = Array.from(grid.children)
  const lastPage = Math.max(Math.ceil(photos.length / pageSize) - 1, 0)
  const prev = pager.querySelector('[data-gallery-page="prev"]')
  const next = pager.querySelector('[data-gallery-page="next"]')
  let page = 0

  const render = () => {
    photos.forEach((photo, index) => {
      photo.classList.toggle('hidden', Math.floor(index / pageSize) !== page)
    })
    prev.disabled = page === 0
    next.disabled = page === lastPage
  }

  const go = (delta) => {
    page = Math.min(Math.max(page + delta, 0), lastPage)
    render()
    panel.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  prev.addEventListener('click', () => go(-1))
  next.addEventListener('click', () => go(1))
  render()
}

const initLightbox = (root) => {
  const lightbox = document.getElementById('galleryLightbox')
  if (!lightbox) return

  const mainEl = lightbox.querySelector('.gallery-lightbox-main')
  const thumbsEl = lightbox.querySelector('.gallery-lightbox-thumbs')
  let main = null
  let thumbs = null

  const close = () => {
    lightbox.classList.add('hidden')
    lightbox.classList.remove('flex')
    document.body.classList.remove('overflow-hidden')
    main?.destroy(true, true)
    thumbs?.destroy(true, true)
    main = null
    thumbs = null
  }

  const open = (panel, index) => {
    const photos = Array.from(panel.querySelectorAll('[data-gallery-open]'))

    const slide = (slideClass, src, alt, imgClass) => {
      const el = document.createElement('div')
      el.className = slideClass
      const img = document.createElement('img')
      img.src = src
      img.alt = alt
      img.className = imgClass
      el.append(img)
      return el
    }

    mainEl.querySelector('.swiper-wrapper').replaceChildren(
      ...photos.map((photo) =>
        slide('swiper-slide h-full', photo.dataset.galleryFull, photo.querySelector('img').alt, 'size-full object-contain')
      )
    )

    thumbsEl.querySelector('.swiper-wrapper').replaceChildren(
      ...photos.map((photo) =>
        slide(
          'swiper-slide !w-28 h-full cursor-pointer opacity-50 transition-opacity [&.swiper-slide-thumb-active]:opacity-100',
          photo.querySelector('img').src,
          '',
          'size-full object-cover'
        )
      )
    )

    lightbox.classList.remove('hidden')
    lightbox.classList.add('flex')
    document.body.classList.add('overflow-hidden')

    thumbs = new Swiper(thumbsEl, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      centeredSlides: true,
      centeredSlidesBounds: true,
      slideToClickedSlide: true,
      watchSlidesProgress: true,
    })

    main = new Swiper(mainEl, {
      modules: [Navigation, Keyboard, Thumbs],
      initialSlide: index,
      keyboard: { enabled: true },
      navigation: {
        prevEl: mainEl.querySelector('.swiper-button-prev'),
        nextEl: mainEl.querySelector('.swiper-button-next'),
      },
      thumbs: { swiper: thumbs },
    })
  }

  root.querySelectorAll('[data-gallery-panel]').forEach((panel) => {
    panel.querySelectorAll('[data-gallery-open]').forEach((photo, index) => {
      photo.addEventListener('click', () => open(panel, index))
    })
  })

  lightbox.querySelector('[data-gallery-close]').addEventListener('click', close)
  lightbox.addEventListener('click', (event) => {
    if (event.target === lightbox) close()
  })
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !lightbox.classList.contains('hidden')) close()
  })
}

const initGallery = () => {
  document.querySelectorAll('[data-gallery]').forEach((root) => {
    initTabs(root)
    initDragScroll(root)
    initImageLoading(root)
    root.querySelectorAll('[data-gallery-panel]').forEach(initPager)
    initLightbox(root)
  })
}

export default initGallery
